import React, { useMemo } from 'react';
import ListBuilder, { ListBuilderItem } from '../fields/ListBuilder';
import { useProductsFlatPage } from '../../../services/api/domains/products/hooks';
import { ProductSummary } from '../../../services/api/domains/manufacturers/types';
import { ManufacturingStatus } from '../../../services/api/domains/products/types';
import { modalStyles as styles } from '../styles';

// Stable reference — mirrors ProductsReview.tsx's own `highLimitParam`
// comment: a fresh object literal on every render would defeat
// useProductsFlat's useMemo(cacheKey, [params]) and useCallback(fetcher,
// [service, params]), re-triggering the fetch effect (and the query's
// setState) every render and looping forever ("Maximum update depth
// exceeded" — caught via a real infinite-loop test hang, not by inspection).
const AVAILABLE_PRODUCTS_PARAMS = { limit: 1000 };

/** Deduped {id, label} options for the "Add Product" picker — mirrors
 * ProductsReview.tsx's `useManufacturersPage({limit: 1000})` bounded-list
 * convention for the reverse (Product modal's manufacturer picker). */
function useAvailableProducts(): ListBuilderItem[] {
  // useProductsFlatPage's return shape has no top-level `.status` — `.data`
  // is already the flattened FlatProduct[] array (see its own useMemo
  // return in products/hooks.ts), unlike a bare useQuery() result. Checking
  // `.status` here (an earlier draft did) silently always returns [] since
  // that field never exists — caught via the "adding a product" test
  // reproducibly leaving the Add button disabled, not by inspection.
  const flatQuery = useProductsFlatPage(AVAILABLE_PRODUCTS_PARAMS);
  return useMemo(() => {
    const seen = new Set<number>();
    const items: ListBuilderItem[] = [];
    for (const row of flatQuery.data) {
      if (seen.has(row.product_id)) {
        continue;
      }
      seen.add(row.product_id);
      items.push({
        id: row.product_id,
        label: row.preferred_product_name || row.product_cas_name,
      });
    }
    return items;
  }, [flatQuery.data]);
}

function toListBuilderItems(products: ProductSummary[]): ListBuilderItem[] {
  return products.map(p => ({
    id: p.product_id,
    label:
      p.display_name || p.preferred_product_name || p.product_cas_name || '',
    status: p.manufacturing_status ?? null,
    fields: {
      man_product_name: p.man_product_name ?? null,
      grade_ids: (p.grade_ids ?? []).map(String),
      grade_name: p.grade_names ?? [],
    },
  }));
}

function fromListBuilderItems(items: ListBuilderItem[]): ProductSummary[] {
  return items.map(item => {
    const gradeIdsField = item.fields?.grade_ids;
    const gradeIds = Array.isArray(gradeIdsField)
      ? Array.from(
          new Set(
            gradeIdsField.map(id => Number(id)).filter(n => Number.isInteger(n))
          )
        )
      : [];
    const manProductNameField = item.fields?.man_product_name;
    return {
      product_id: item.id,
      product_cas_name: null,
      display_name: item.label,
      manufacturing_status: (item.status as ManufacturingStatus) ?? null,
      man_product_name:
        typeof manProductNameField === 'string' ? manProductNameField : null,
      grade_ids: gradeIds,
    };
  });
}

interface ProductsPanelProps {
  products: ProductSummary[];
  onChange: (products: ProductSummary[]) => void;
  isEditMode: boolean;
}

/**
 * Manufacturer modal Products panel (issue #653, Task 10, Sub-task 10.4).
 * Mirrors the Product modal's manufacturer panel (products' `list_builder`
 * FieldConfig, `createListBuilderField` in
 * client/src/domains/products/adapters/modalFieldAdapter.ts) — same shared
 * `ListBuilder` component, same three editable columns (Manufacturing
 * Status, Product Type via grade_ids, manufacturer-specific Product Name),
 * same client-side-staged add/remove deferred to the modal's Save button.
 * Built as a standalone component (not a FieldConfig registry entry) to
 * match this feature's other Task 10 tabs/sections.
 */
const ProductsPanel: React.FC<ProductsPanelProps> = ({
  products,
  onChange,
  isEditMode,
}) => {
  const availableItems = useAvailableProducts();
  const value = useMemo(() => toListBuilderItems(products), [products]);

  const handleChange = (items: ListBuilderItem[]) => {
    onChange(fromListBuilderItems(items));
  };

  // defaultCollapsed mirrors the product modal's own convention for every
  // one of its CollapsibleSection-wrapped source-link panels (OrangeBookPanel/
  // DMFPanel/NDCPanel all use `records.length > 1` — issue #653, Task 16 QA
  // follow-up) rather than always starting expanded regardless of count.
  // ListBuilder now owns its own collapse toggle directly (Task 16 QA
  // follow-up) instead of being wrapped in a separate CollapsibleSection —
  // the item count and expand/collapse control live right on ListBuilder's
  // own "PRODUCTS"-badged header instead of a second header stacked above it.
  return (
    <ListBuilder
      id="manufacturer-products"
      label="Products"
      value={value}
      onChange={handleChange}
      availableItems={availableItems}
      disabled={!isEditMode}
      styles={styles}
      collapsible
      defaultCollapsed={products.length > 1}
      config={{
        idKey: 'Product',
        labelKey: 'Product',
        badgeText: 'PRODUCTS',
        statusKey: 'Manufacturing Status',
        statusOptions: [
          { value: 'Commercial', label: 'Commercial' },
          { value: 'R&D', label: 'R&D' },
        ],
        statusColumns: [
          {
            kind: 'async-multi',
            key: 'grade_ids',
            label: 'Product Type',
            asyncFetch: '/grades',
            asyncSearchField: 'grade_name',
            asyncValueField: 'grade_id',
            asyncLabelField: 'grade_name',
          },
          {
            kind: 'text',
            key: 'man_product_name',
            label: 'Manufacturer Product Name',
            maxLength: 450,
          },
        ],
        addButtonText: 'Add Product',
        addModalTitle: 'Add Products',
        noItemsText: 'No products associated with this manufacturer',
      }}
    />
  );
};

export default ProductsPanel;
